// Formateo del dashboard de cobranza (GET billing/collections/dashboard).
//
// Los números los calcula billing sobre TODO el operador; acá solo se ordenan y se
// preparan para la UI. Funciones puras: se testean sin montar componentes.

// Orden de los tramos de antigüedad tal como los define billing. La suma de los 7 es
// exactamente el total impago, así que no se omite ninguno aunque venga en cero.
export const AGING_BUCKETS = [
  'in_review',
  'no_due_date',
  'not_due',
  '1_30',
  '31_60',
  '61_90',
  '90_plus',
];

// Claves i18n (el texto en español ES la clave): el componente las pasa por t().
export const AGING_BUCKET_LABELS = {
  in_review: 'En revisión',
  no_due_date: 'Sin vencimiento',
  not_due: 'No vencidas',
  '1_30': '1–30 d',
  '31_60': '31–60 d',
  '61_90': '61–90 d',
  '90_plus': 'Más de 90 d',
};

const OVERDUE_BUCKETS = new Set(['1_30', '31_60', '61_90', '90_plus']);

/**
 * Moneda en la que se muestran los montos del dashboard.
 *
 * Billing arma el ranking de morosos en su "moneda principal" (la de más notas impagas);
 * se toma esa para que los KPIs y el ranking hablen de la misma moneda. Sin morosos, la del
 * operador si tiene deuda en ella; si no, la primera con deuda; si no hay deuda, la del
 * operador.
 *
 * @param {object|null} dashboard respuesta del endpoint
 * @param {string} operatorCurrency moneda del operador (ISO 4217)
 * @returns {string}
 */
export function resolveDashboardCurrency(dashboard, operatorCurrency = 'CLP') {
  const fromDebtors = dashboard?.topDebtors?.[0]?.currency;
  if (fromDebtors) return fromDebtors;
  const withDebt = Object.keys(dashboard?.unpaid?.amountByCurrency ?? {}).sort();
  if (withDebt.includes(operatorCurrency)) return operatorCurrency;
  return withDebt[0] ?? operatorCurrency;
}

/**
 * Separa un `amountByCurrency` en el monto de `currency` y el resto de las monedas con
 * monto distinto de cero (sumar monedas distintas no tiene sentido, así que no se suman).
 *
 * @param {Record<string, number>|undefined} amountByCurrency
 * @param {string} currency
 * @returns {{ amount: number, others: Array<{ currency: string, amount: number }> }}
 */
export function splitAmounts(amountByCurrency, currency) {
  const entries = Object.entries(amountByCurrency ?? {});
  return {
    amount: Number(amountByCurrency?.[currency]) || 0,
    others: entries
      .filter(([c, amount]) => c !== currency && Number(amount) !== 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([c, amount]) => ({ currency: c, amount: Number(amount) })),
  };
}

/**
 * Filas del gráfico de antigüedad, SIEMPRE los 7 tramos en el orden de billing (un tramo
 * ausente de la respuesta cuenta como cero).
 *
 * @param {Array<{ bucket: string, notes: number, amountByCurrency: Record<string, number> }>} aging
 * @param {string} currency moneda que se grafica
 * @returns {Array<{ bucket: string, label: string, overdue: boolean, notes: number,
 *                   amount: number, others: Array<{ currency: string, amount: number }> }>}
 */
export function buildAgingRows(aging, currency) {
  const byBucket = new Map((Array.isArray(aging) ? aging : []).map((row) => [row.bucket, row]));
  return AGING_BUCKETS.map((bucket) => {
    const row = byBucket.get(bucket);
    const { amount, others } = splitAmounts(row?.amountByCurrency, currency);
    return {
      bucket,
      label: AGING_BUCKET_LABELS[bucket],
      overdue: OVERDUE_BUCKETS.has(bucket),
      notes: Number(row?.notes) || 0,
      amount,
      others,
    };
  });
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Instante del cálculo ('dd-mm-aaaa HH:mm', hora del navegador: es un instante, no un día
 * del operador). Cadena vacía si no es una fecha válida.
 */
export function formatAsOf(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Días con un decimal y coma decimal ('12,5'); '—' si no hay dato. */
export function formatDays(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return (Math.round(Number(value) * 10) / 10).toFixed(1).replace('.', ',').replace(/,0$/, '');
}

/** Proporción 0..1 → porcentaje entero (0.25 → 25). null si no hay dato. */
export function toPercent(share) {
  if (share === null || share === undefined || !Number.isFinite(Number(share))) return null;
  return Math.round(Number(share) * 100);
}
